import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { DoczHandle } from "@norkron/web-docz";
import {
  type CanvasSurfaceHandle,
  createCanvasSurface,
  createWasmRelayout,
  DocumentMirror,
  DoczEditBridge,
  type HighlightRect,
  loadPageLayout,
  makeStructuredEncodeBlocks,
  mirrorBlocksFromHandle,
  type PageLayoutModule,
  type PageRect,
} from "@norkron/web-page-layout";
import type { AnnotationDraft, Comment } from "../../api/types";
import type { AnchorState } from "../../lib/anchoring";
import { articleApi } from "../../api/articleUnits";
import { buildDoczHandleFromBinary } from "../../lib/doczHandle";
import { useDevicePixelRatio } from "../../lib/useDevicePixelRatio";
import { CANVAS_EDIT_PAGE_CONFIG } from "./canvasPageConfig";
import {
  applyCanvasScale,
  DOCUMENT_BLOCK_MARGIN_CSS,
} from "./canvasPresentation";
import type { ScrollIntent } from "../../lib/scrollMotion";
import {
  BLOOM_DURATION_MS,
  commentPaint,
  createProbeResolver,
  withAlpha,
} from "../../lib/canvasCommentColors";
import {
  commentIdAtPoint,
  createFlashController,
  emphasisedId,
  type PendingBand,
  planHighlights,
  recolorHighlights,
  withPendingBand,
} from "./readCanvasHighlights";
import {
  anchorPointsOf,
  blockOrdForRef,
  draftFromCanvasSelection,
  focusAnchorOnSurface,
  type ReadBlock,
  type ReadCanvasAnchor,
  resolveReadCanvasAnchors,
  type ScrollableSurface,
} from "./readCanvasAnchors";
import type { SurfacePointMap } from "../../lib/reviewOrder";

/**
 * `<ReadCanvas>` — READ MODE ON THE CANVAS ENGINE (P3-8).
 *
 * Read mode was the last DOM document surface in the product: `useDoczCanvas`
 * paints the server's `rendered_html` with `el.innerHTML = html`. That surface
 * is the one every reviewer looks at, so it is also the one that made "one
 * document technology" untrue. This component paints the SAME document through
 * the SAME `pl_layout` pass that produces the PDF and drives the paginated
 * editor — `interactivity: 'read'`, so the engine is identical and only mutation
 * is switched off (no input sink is installed and the bridge is wrapped in
 * `readOnlyBridge`, upstream).
 *
 * RULE ZERO / HR13. Nothing here re-implements the surface: the mount, the
 * cross-page drag-select, double-click word select, ⌘A, the IR-sliced copy and
 * the highlight/selection overlays are `@norkron/web-page-layout`'s
 * `createCanvasSurface`; the accessible projection is its painter-agnostic
 * `DocumentMirror` (do NOT rebuild it — it is what keeps the content readable to
 * assistive technology once the pixels stop being text). This file is the
 * elaborate-specific glue only: fetch the server docz transport, wire the
 * comment anchors, and report failure honestly.
 *
 * IT DOES NOT REPLACE THE DOM PATH. `<DoczCanvas>` mounts both and the
 * `readCanvas` flag chooses which is visible, so parity can be compared
 * side-by-side and a rollback is a query-string edit. See `lib/readCanvasFlag`.
 *
 * WHAT DOES NOT COME ACROSS — stated, never hidden. Browser find-in-page (⌘F),
 * native copy of a DOM range, print (⌘P), browser translation, extensions/user
 * stylesheets and browser text zoom are DOM affordances a `<canvas>` does not
 * have. `DocumentMirror` keeps the document readable to assistive technology;
 * it does not restore any of the above (a mirror clipped to 1px is not something
 * a sighted user can read, and native ⌘F over it scrolls to an invisible box).
 * Selection + copy ARE restored here, from the IR rather than from the pixels.
 */

/** The imperative surface the workbench drives (mirrors `useDoczCanvas`'s). */
export interface ReadCanvasHandle {
  /** Scroll to + report a comment's anchor. `null` when the quote is gone.
   *  `intent` defaults to `"navigate"` — a reviewer click, animated (and
   *  instant under `prefers-reduced-motion`); a deep-link landing passes
   *  `"restore"`, which jumps. */
  focusAnchor(
    commentId: number | string,
    intent?: ScrollIntent,
  ): AnchorState | null;
  /** The live block projection the anchors resolve against (test/E2E seam). */
  blocks(): readonly ReadBlock[];
  /** rich-comments §3.2: update the PENDING band's type (→ its preview colour)
   *  as the reviewer changes the composer's type pick. No-op with no pending
   *  band. The canvas twin of `useDoczCanvas().setPendingType`. */
  setPendingType(type: string): void;
  /** rich-comments §3.2: clear the pending selection band (cancel / submit). */
  clearPending(): void;
  /** rich-comments §13.1: card→span link — paint the given comment's band in its
   *  GLOW, or clear with `null`. Imperative: it recolours the CACHED bands, so a
   *  gutter hover costs no wasm geometry. */
  setLinkedHover(commentId: number | null): void;
  /**
   * Re-emit the LIVE selection through `onSelectText`, returning whether there
   * was one. This is the toolbar's "New comment" in read mode: the reviewer
   * selects, then presses a button, so the composer has to be openable from
   * something other than the selection event itself.
   *
   * The DOM surface did this by calling its `onMouseUp` a second time, which
   * re-read `window.getSelection()`. A canvas selection lives in the engine, not
   * in the document, so it is asked for by name.
   */
  requestComment(): boolean;
  /**
   * The VIEWPORT rect of a whole block, by its stored `block_ref` — or `null`
   * when no block owns that ref (or the surface is not painted).
   *
   * Used to place a popover against a section's heading. On the DOM surface this
   * was `querySelector('[data-block-id=…]').getBoundingClientRect()`; a canvas
   * has no such element, so the geometry is asked of the engine in the one
   * coordinate that does exist.
   */
  rectForBlockRef(blockRef: string): DOMRect | null;
}

export interface ReadCanvasProps {
  /** The document whose docz transport is laid out (`GET /documents/:id/docz`). */
  readonly docId: string | number;
  /** The comment set to anchor. Only ROOTS with a quote paint a highlight. */
  readonly comments: readonly Comment[];
  /** Whether this surface is mounted/visible (a hidden pane does no wasm work). */
  readonly active?: boolean;
  /** Accessible name for the mirror's `role="document"` landmark. */
  readonly title?: string;
  /** BCP-47 language of the document — the mirror's `lang`, so a German
   *  deliverable is not read out in an English voice (WCAG 3.1.2). */
  readonly lang?: string;
  /** Fired when a canvas selection settles into a durable draft (or `null`). */
  readonly onSelectText?: (
    draft: AnnotationDraft | null,
    rect: DOMRect | null,
  ) => void;
  /** Fired after every re-anchor pass with the per-comment anchor states — the
   *  same map `useDoczCanvas` publishes, so the review rail is fed identically. */
  readonly onAnchorStates?: (states: Record<number, AnchorState>) => void;
  /** Fired after every re-anchor pass with WHERE each resolved note sits on this
   *  surface — the review rail's "Document order". The canvas twin of
   *  `useDoczCanvas().anchorPoints`, and published as a callback for the same
   *  reason the states are: the host holds it as state and merges the panes. */
  readonly onAnchorPoints?: (points: SurfacePointMap) => void;
  /** Fired when the reviewer CLICKS commented text — the canvas twin of the DOM
   *  `.docz-comment` span's `cursor: pointer` click, which opened its thread.
   *  `null` when they clicked text that carries no comment (a deselect). */
  readonly onSelectComment?: (commentId: number | null) => void;
  /** rich-comments §13.2: fired as the pointer enters/leaves a comment's painted
   *  band (span→card link), with the hovered comment id or `null`. The canvas
   *  twin of the DOM surface's delegated `mouseover`/`mouseout` listener. */
  readonly onHoverComment?: (commentId: number | null) => void;
  /** Page scale (1 = 100%), honoured verbatim and resolved through the ONE
   *  app-wide policy in `canvasPresentation` — the same one the edit surfaces
   *  resolve through, so a read sheet and an edit sheet are the same size.
   *
   *  It no longer mentions the `compare` harness: that harness went with the
   *  DOM read surface in the 2026-07-28 pivot (`lib/readCanvasFlag`), and the
   *  auto-shrink it justified was what made read and edit differ. `<DoczCanvas>`
   *  is the only caller and it passes 1. */
  readonly zoom?: number;
  /** Bump to RE-FETCH the document. The transport is fetched ONCE at mount and
   *  `docId` does not change when the document does, so without this a Save
   *  would leave the canvas reader painting the pre-save body — the same
   *  stale-read defect the DOM path had, just moved to a new painter. The
   *  workbench bumps it on every accepted save. */
  readonly revision?: number;
  readonly className?: string;
  readonly testId?: string;
  /** DI seam (tests / E2E): resolve the document into a live `DoczHandle`. */
  readonly loadHandle?: (docId: string) => Promise<DoczHandle>;
  /** DI seam (tests / E2E): resolve the `pdfz-layout.wasm` engine. */
  readonly loadEngine?: () => Promise<PageLayoutModule>;
}

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "ready" }
  | { readonly status: "error"; readonly message: string };

async function defaultLoadHandle(docId: string): Promise<DoczHandle> {
  // The CANVAS mounts the BINARY transport: raw block-DTO bytes straight into
  // web-docz's decoder. The JSON terminal stays registered for the legacy DOM
  // editor — this is not its replacement, it is the canvas's own wire.
  return buildDoczHandleFromBinary(await articleApi.getDoczBinary(docId));
}

/** Resolve the selection tint into a CONCRETE colour — `fillStyle` cannot parse
 *  `var(--x)` and the assignment is a SILENT no-op (see `canvasCommentColors`). */
function resolveSelectionColor(host: HTMLElement): string {
  const accent = createProbeResolver(host)("--norkron-accent");
  return withAlpha(accent, 0.3) ?? "rgba(37, 99, 235, 0.3)";
}

/** The engine's fallback tint for a rect that carries no `color` of its own.
 *  Every rect this component emits DOES carry one; this only stops the engine
 *  falling back to its hardcoded amber if one ever slips through. */
function resolveHighlightColor(host: HTMLElement): string {
  return commentPaint({
    type: "comment",
    state: "exact",
    resolve: createProbeResolver(host),
  }).rest;
}

export const ReadCanvas = forwardRef<ReadCanvasHandle, ReadCanvasProps>(
  function ReadCanvas(props, ref) {
    const {
      docId,
      comments,
      active = true,
      title,
      lang,
      onSelectText,
      onAnchorStates,
      onAnchorPoints,
      onSelectComment,
      onHoverComment,
      zoom = 1,
      revision = 0,
      className,
      testId = "read-canvas",
      loadHandle = defaultLoadHandle,
      loadEngine = loadPageLayout,
    } = props;

    const dpr = useDevicePixelRatio(2);
    const mountRef = useRef<HTMLDivElement>(null);
    const surfaceRef = useRef<CanvasSurfaceHandle | null>(null);
    const mirrorRef = useRef<DocumentMirror | null>(null);
    const blocksRef = useRef<readonly ReadBlock[]>([]);
    const anchorsRef = useRef<readonly ReadCanvasAnchor[]>([]);
    /** The comment whose span is painted in its GLOW — the canvas equivalent of
     *  the DOM span's `:hover` / `[data-linked-hover]` state. */
    const selectedRef = useRef<string | null>(null);
    /** The PAINTED bands, per comment. The click hit-test runs against exactly
     *  these, in the same page space the engine drew them in, so "what the reader
     *  sees is what they hit" is true by construction rather than by arithmetic. */
    const rectsRef = useRef<ReadonlyMap<string, readonly PageRect[]>>(new Map());
    /** The comment under the pointer — the DOM `:hover` rule, restored. */
    const hoveredRef = useRef<string | null>(null);
    /** The comment the GUTTER is hovering (card→span). Driven from outside via
     *  `setLinkedHover`; the DOM surface expressed this as `[data-linked-hover]`. */
    const linkedRef = useRef<string | null>(null);
    /**
     * The live PENDING selection band — "you are commenting on this" — kept as a
     * synthetic {@link ReadCanvasAnchor} so it rides the SAME planner, painter and
     * colour policy every saved comment does (Rule Zero: no second paint path).
     * `type` tracks the composer's pick so the preview recolours as the reviewer
     * changes it.
     *
     * WHAT DOES NOT COME ACROSS, stated: the DOM pending highlight also pulsed
     * (`@keyframes ela-pending-pulse`, annotations.css:69). A canvas band has no
     * CSS animation, and inventing a repaint loop for it would be motion this
     * module owns rather than the engine — so the pending band is steady, and the
     * pulse is a real, named loss rather than a silent one.
     */
    const pendingRef = useRef<PendingBand | null>(null);
    /** One bloom at a time (two overlapping washes composite into neither). */
    const flashRef = useRef(createFlashController());
    /** Publish the given (or live) selection to the host. Installed by the mount
     *  effect, which is the only scope that holds the surface. */
    const emitSelectionRef = useRef<(() => boolean) | null>(null);
    const [load, setLoad] = useState<LoadState>({ status: "loading" });

    // Keep the callbacks AND the comment set current without re-running the
    // mount effect. This is not tidiness: a comment set that arrives async (it
    // always does) would otherwise tear the surface down and rebuild it —
    // re-running the whole wasm layout and throwing away the reader's scroll
    // position — every time a note is added, resolved or streamed in.
    const cbRef = useRef({
      onSelectText,
      onAnchorStates,
      onSelectComment,
      onAnchorPoints,
      onHoverComment,
    });
    cbRef.current = {
      onSelectText,
      onAnchorStates,
      onSelectComment,
      onAnchorPoints,
      onHoverComment,
    };
    const commentsRef = useRef(comments);
    commentsRef.current = comments;

    /**
     * Re-resolve every comment against the LIVE block text and repaint the
     * highlight bands. Byte offsets are stable across relayout; pixels are not,
     * so the rects are always recomputed rather than cached.
     *
     * PARITY, and the defect this closes. The previous version flattened every
     * anchor into one anonymous rect array, so the engine painted ONE fallback
     * amber over the whole document: a request-for-change and a note were
     * indistinguishable in the text while their cards were red and blue. Each
     * rect now carries its own comment's fill, taken from the LIVE theme tokens
     * (`lib/canvasCommentColors`) so light and dark each get their own alphas.
     */
    /** The band that takes the glow — precedence lives in `readCanvasHighlights`. */
    const emphasised = (): string | null =>
      emphasisedId(hoveredRef.current, linkedRef.current, selectedRef.current);

    /** Saved anchors + the live pending band (see `withPendingBand`). */
    const paintAnchors = (): readonly ReadCanvasAnchor[] =>
      withPendingBand(anchorsRef.current, pendingRef.current);

    /** A per-pass palette over the LIVE theme tokens (memoised inside), so a
     *  mid-session theme switch repaints in the new numbers without a remount. */
    const painterForPass = (mount: HTMLElement) => {
      const resolve = createProbeResolver(mount);
      return (a: ReadCanvasAnchor) =>
        commentPaint({ type: a.type, state: a.state, resolve });
    };

    const repaintHighlights = useCallback((): void => {
      const surface = surfaceRef.current;
      const mount = mountRef.current;
      if (!surface || !mount) return;
      const plan = planHighlights<PageRect>(
        surface,
        paintAnchors(),
        painterForPass(mount),
        emphasised(),
      );
      rectsRef.current = plan.byComment;
      // Typed against the ENGINE's contract, not a local shape: a compile-time
      // check that every band really carries its own concrete `color`.
      const painted: readonly HighlightRect[] = plan.rects;
      surface.setHighlights(painted);
    }, []);

    /** Re-tint the CACHED bands. No geometry, no wasm — this is what makes the
     *  hover rule affordable on a canvas (see `recolorHighlights`). */
    const recolorFromCache = useCallback((): void => {
      const surface = surfaceRef.current;
      const mount = mountRef.current;
      if (!surface || !mount) return;
      const painted: readonly HighlightRect[] = recolorHighlights<PageRect>(
        rectsRef.current,
        paintAnchors(),
        painterForPass(mount),
        emphasised(),
      );
      surface.setHighlights(painted);
    }, []);

    const reanchor = useCallback((): void => {
      const surface = surfaceRef.current;
      if (!surface) return;
      const { anchors, states } = resolveReadCanvasAnchors(
        blocksRef.current,
        commentsRef.current,
      );
      anchorsRef.current = anchors;
      // A selection whose comment no longer resolves must not keep a stale span
      // glowing somewhere else in the document.
      if (
        selectedRef.current !== null &&
        !anchors.some((a) => a.commentId === selectedRef.current)
      ) {
        selectedRef.current = null;
      }
      repaintHighlights();
      cbRef.current.onAnchorStates?.(states);
      // WHERE each note landed, from the SAME anchors the bands were painted
      // from — so the rail's reading order and the paper cannot disagree.
      cbRef.current.onAnchorPoints?.(anchorPointsOf(anchors));
    }, [repaintHighlights]);

    /** The bloom policy for ONE anchor, in the live theme's colours. */
    const bloomFor = useCallback((a: ReadCanvasAnchor) => {
      const mount = mountRef.current;
      if (!mount) return null;
      return {
        color: commentPaint({
          type: a.type,
          state: a.state,
          resolve: createProbeResolver(mount),
        }).bloom,
        // Stated, not defaulted: 1.4s is the DOM `ela-anno-bloom` keyframe.
        durationMs: BLOOM_DURATION_MS,
        onStart: (cancel: () => void) => flashRef.current.start(() => cancel),
      };
    }, []);

    useEffect(() => {
      const mount = mountRef.current;
      if (!mount || !active) return;
      let disposed = false;
      let cleanupClick: (() => void) | null = null;
      setLoad({ status: "loading" });

      void (async () => {
        try {
          const [handle, module] = await Promise.all([
            loadHandle(String(docId)),
            loadEngine(),
          ]);
          if (disposed) return;

          const bridge = new DoczEditBridge(handle);
          // The blocks the geometry index is built over are the bridge's CANVAS
          // VIEW (a list flattens to one block per item), not `handle.blocks` —
          // so the anchors must resolve against the same projection or an
          // ordinal means two different things at the two ends.
          blocksRef.current = bridge.blocks.map((b) =>
            b.id != null ? { id: b.id, text: b.text } : { text: b.text },
          );

          const relayout = createWasmRelayout({
            module,
            encodeBlocks: makeStructuredEncodeBlocks(),
            // The SAME plate geometry the paginated editor and the branded PDF
            // lay out with, so a reviewer's page breaks are the client's page
            // breaks. `justify` rides along deliberately: it is the only wrap
            // strategy pdfz stamps per-line provenance on, and without
            // provenance there is no hit-test — i.e. no "select → comment",
            // which is the entire point of read mode. That the canvas therefore
            // justifies a ragged document is a REAL fidelity cost, closable only
            // by the pdfz provenance FR.
            config: CANVAS_EDIT_PAGE_CONFIG,
          });

          const surface = createCanvasSurface(mount, {
            interactivity: "read",
            bridge,
            relayout,
            zoom,
            dpr,
            maxDpr: 2,
            // The page chain clears the top of its scroller instead of butting
            // against it. Passed rather than styled because the painter writes
            // this margin INLINE and re-applies it on every re-plan — see
            // `DOCUMENT_BLOCK_MARGIN_CSS`. The past-revision surface composes
            // this component, so it inherits the same value by construction.
            blockMarginCss: DOCUMENT_BLOCK_MARGIN_CSS,
            // No caret exists on a read surface (the surface does not even
            // create the caret canvas), so the caret colour is inert — stated
            // as `transparent` rather than left to a default that would imply
            // a caret is coming.
            colors: {
              caret: "transparent",
              selection: resolveSelectionColor(mount),
              // SUPPLIED, and it was not before. Omitting `highlight` makes the
              // engine skip its CSS-variable token entirely and fall back to a
              // hardcoded amber — which is exactly the one flat colour every
              // comment used to paint.
              highlight: resolveHighlightColor(mount),
            },
            onSelect: (sel) => {
              publishSelection(sel);
            },
          });
          surfaceRef.current = surface;

          // The ONE selection→composer path, shared by the engine's `onSelect`
          // and the toolbar's "New comment" (`requestComment`), so a comment
          // opened from the button and one opened by dragging cannot disagree
          // about the draft, the pending band or the popover placement.
          function publishSelection(
            sel: Parameters<
              NonNullable<Parameters<typeof createCanvasSurface>[1]["onSelect"]>
            >[0],
          ): boolean {
            {
              const cb = cbRef.current.onSelectText;
              if (!sel) {
                // A dismissed selection takes the pending preview with it —
                // otherwise a band saying "you are commenting on this" outlives
                // the composer that was commenting.
                if (pendingRef.current) {
                  pendingRef.current = null;
                  repaintHighlights();
                }
                cb?.(null, null);
                return false;
              }
              const draft = draftFromCanvasSelection(blocksRef.current, sel);
              if (!draft) {
                if (pendingRef.current) {
                  pendingRef.current = null;
                  repaintHighlights();
                }
                cb?.(null, null);
                return false;
              }
              // §3.2 — materialise the settled selection as a type-coloured
              // PENDING band BEFORE the composer opens. On the DOM surface the
              // native selection vanished the instant focus moved into the
              // composer, leaving the reviewer typing about text with no visible
              // mark; the canvas selection overlay has exactly the same problem,
              // so the band is what keeps "this is what you are commenting on"
              // on screen. Default type `comment`; the composer drives
              // `setPendingType` as the reviewer picks.
              const slicesForBand = sel.blocks?.length ? sel.blocks : [sel];
              pendingRef.current = {
                type: "comment",
                bands: slicesForBand.map((s) => ({
                  blockOrd: s.blockOrd,
                  start: s.start,
                  end: s.end,
                })),
              };
              repaintHighlights();
              if (!cb) return true;
              // The composer is placed over the WHOLE range. Measuring only
              // `(blockOrd, start, end)` reads those two offsets as if they were
              // in one block — for a range dragged across a paragraph and a table
              // they are not, and the popup lands somewhere that is neither end.
              const slices = sel.blocks?.length ? sel.blocks : [sel];
              const rects = slices.flatMap((s) =>
                surface.rangeRects(s.blockOrd, s.start, s.end),
              );
              cb(draft, surface.viewportRectFor(rects));
              return true;
            }
          }
          emitSelectionRef.current = () =>
            publishSelection(surfaceRef.current?.selection() ?? null);

          // ── CLICK A COMMENTED SPAN → OPEN ITS THREAD ────────────────────────
          // On the DOM read view every `.docz-comment` span carried
          // `cursor: pointer` and a click opened its card. On the canvas there is
          // no span and no click target — the only way back to a comment id is
          // the engine's `positionAt`, which is a PURE client-point → document
          // position lookup (it must not move the selection; a click that both
          // opens a thread and collapses the caret is two gestures at once).
          //
          // A click that ENDED A DRAG is a select-to-comment gesture, not an
          // open-this-thread gesture, so a live selection suppresses it — without
          // that guard, finishing a selection inside an already-commented sentence
          // would open the old thread on top of the composer.
          const onCanvasClick = (ev: MouseEvent): void => {
            const s = surfaceRef.current;
            if (!s) return;
            if (s.selection()) return;
            // The PENDING band is not a thread and must never be "opened" — the
            // DOM surface excluded `data-comment-id="pending"` for the same
            // reason. Resolved against the saved anchors only.
            const id = commentIdAtPoint(
              anchorsRef.current,
              rectsRef.current,
              s.pagePointAt(ev.clientX, ev.clientY),
            );
            if (id === null && selectedRef.current === null) return;
            selectedRef.current = id;
            repaintHighlights();
            cbRef.current.onSelectComment?.(id === null ? null : Number(id));
          };
          mount.addEventListener("click", onCanvasClick);

          // ── HOVER: the DOM `:hover` rule, and the `cursor: pointer` that made
          // the span look clickable in the first place. Without the cursor there
          // is no way to DISCOVER that the text above is interactive; without the
          // glow there is no feedback that the pointer is on the right words.
          //
          // Guarded on CHANGE, and repainted from the cached bands, so moving the
          // pointer across a document costs one hit-test per event and one
          // overlay repaint per comment boundary crossed — never a re-measure.
          const onCanvasMove = (ev: MouseEvent): void => {
            const s = surfaceRef.current;
            if (!s) return;
            const id = commentIdAtPoint(
              anchorsRef.current,
              rectsRef.current,
              s.pagePointAt(ev.clientX, ev.clientY),
            );
            if (id === hoveredRef.current) return;
            hoveredRef.current = id;
            mount.style.cursor = id === null ? "" : "pointer";
            recolorFromCache();
            // span→card: the rail brightens the matching note's card.
            cbRef.current.onHoverComment?.(id === null ? null : Number(id));
          };
          const onCanvasLeave = (): void => {
            if (hoveredRef.current === null) return;
            hoveredRef.current = null;
            mount.style.cursor = "";
            recolorFromCache();
            cbRef.current.onHoverComment?.(null);
          };
          mount.addEventListener("mousemove", onCanvasMove);
          mount.addEventListener("mouseleave", onCanvasLeave);
          cleanupClick = () => {
            mount.removeEventListener("click", onCanvasClick);
            mount.removeEventListener("mousemove", onCanvasMove);
            mount.removeEventListener("mouseleave", onCanvasLeave);
            mount.style.cursor = "";
          };

          // The accessible projection — painter-agnostic, IR-derived, and the
          // ONLY reason a canvas document is readable to assistive technology.
          // Reused verbatim from the upstream lib; never rebuilt here.
          //
          // `exposure: 'exclusive'` is LOAD-BEARING and must never be dropped
          // here. The mirror's default is `'silent'` (present but `aria-hidden`),
          // which is right when it shadows a surface that is already accessible
          // HTML — the DOM read view — because two exposed copies would read the
          // document to a screen reader twice. This surface is a `<canvas>`: it
          // has NO accessibility tree at all, so a silent mirror over it would
          // leave the document with no representation whatsoever. Here the mirror
          // IS the document, and it says so.
          mirrorRef.current = new DocumentMirror(mount, {
            ...(title !== undefined ? { label: title } : {}),
            ...(lang !== undefined ? { lang } : {}),
            exposure: "exclusive",
            testId: `${testId}-mirror`,
          });
          mirrorRef.current.update(mirrorBlocksFromHandle(handle));

          reanchor();
          // THE SCALE. Resolved through the ONE presentation policy every canvas
          // document surface in the app shares (`canvasPresentation`) — never
          // decided here. Until 2026-07-29 this line was a private `fitToWidth()`
          // that no editor had, so the identical A4 sheet painted at 100% while
          // editing and shrunk-to-fit while reading. Read the module doc for what
          // the policy is, what dropping the auto-fit costs, and where it returns
          // if it returns.
          applyCanvasScale(mount, surface, zoom);
          const fonts = (globalThis as { document?: { fonts?: FontFaceSet } })
            .document?.fonts;
          if (fonts?.ready) {
            void fonts.ready.then(() => {
              if (!disposed) surfaceRef.current?.repaint();
            });
          }
          setLoad({ status: "ready" });
        } catch (err) {
          if (disposed) return;
          setLoad({
            status: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      })();

      return () => {
        disposed = true;
        emitSelectionRef.current = null;
        cleanupClick?.();
        flashRef.current.cancel();
        surfaceRef.current?.destroy();
        surfaceRef.current = null;
        // DESTROY the mirror, do not merely drop the reference. It appends a
        // real `<article>` to the mount and reconciles it; dropping the ref left
        // that subtree in the document, so every remount (a save bumping
        // `revision`, a pane swap, a version round trip) added ANOTHER full copy
        // of the document to the accessibility tree. A screen reader then read
        // the document as many times as the reader had re-rendered it.
        mirrorRef.current?.destroy();
        mirrorRef.current = null;
        blocksRef.current = [];
        anchorsRef.current = [];
        selectedRef.current = null;
        hoveredRef.current = null;
        linkedRef.current = null;
        pendingRef.current = null;
        rectsRef.current = new Map();
      };
      // `revision` is a REAL dependency, not a lint appeasement: it is the only
      // thing that changes when the DOCUMENT changes under a stable `docId`.
    }, [
      docId,
      revision,
      active,
      dpr,
      zoom,
      title,
      lang,
      testId,
      loadHandle,
      loadEngine,
      reanchor,
      repaintHighlights,
      recolorFromCache,
    ]);

    // Re-anchor whenever the comment set changes (an SSE arrival, a new note) —
    // WITHOUT remounting the surface.
    useEffect(() => {
      if (load.status === "ready") reanchor();
    }, [load.status, comments, reanchor]);

    useImperativeHandle(
      ref,
      (): ReadCanvasHandle => ({
        focusAnchor: (commentId, intent) => {
          // Cancel BEFORE the next bloom starts: the engine may hold one flash
          // slot, and cancelling afterwards could cancel the new one.
          flashRef.current.cancel();
          return focusAnchorOnSurface(
            surfaceRef.current as ScrollableSurface<PageRect> | null,
            anchorsRef.current,
            commentId,
            intent,
            bloomFor,
          );
        },
        blocks: () => blocksRef.current,
        setPendingType: (type) => {
          const p = pendingRef.current;
          if (!p || p.type === type) return;
          pendingRef.current = { ...p, type };
          // A FULL repaint, not a recolour: `recolorHighlights` re-tints the
          // CACHED rect map, and the pending band's rects are only in that map
          // because the last plan put them there under a different type. Going
          // through the planner keeps one source of truth for what is painted.
          repaintHighlights();
        },
        clearPending: () => {
          if (!pendingRef.current) return;
          pendingRef.current = null;
          repaintHighlights();
        },
        requestComment: () => emitSelectionRef.current?.() ?? false,
        rectForBlockRef: (blockRef) => {
          const surface = surfaceRef.current;
          if (!surface) return null;
          const ord = blockOrdForRef(blocksRef.current, blockRef);
          if (ord < 0) return null;
          const text = blocksRef.current[ord]?.text ?? "";
          const bytes = new TextEncoder().encode(text).length;
          return surface.viewportRectFor(surface.rangeRects(ord, 0, bytes));
        },
        setLinkedHover: (commentId) => {
          const next = commentId === null ? null : String(commentId);
          if (linkedRef.current === next) return;
          linkedRef.current = next;
          // The CHEAP path — a gutter hover must not re-measure geometry.
          recolorFromCache();
        },
      }),
      [bloomFor, repaintHighlights, recolorFromCache],
    );

    return (
      <div
        className={className ? `ela-read-canvas ${className}` : "ela-read-canvas"}
        data-testid={testId}
        data-read-surface="canvas"
        data-load-state={load.status}
      >
        <div className="ela-read-canvas__pages" ref={mountRef} />
        {load.status === "error" ? (
          // An honest failure, not an empty pane. Read mode goes from ZERO bytes
          // of wasm to a mandatory ~1.7 MB engine + font faces before the first
          // glyph, so this path is reachable in the field (blocked wasm, an
          // offline asset, a transport 5xx) and it names the way back.
          <div className="ela-read-canvas__error" role="alert">
            <p>
              The canvas reader could not load this document, so nothing above is
              painted.
            </p>
            <p className="ela-read-canvas__detail">{load.message}</p>
            <p>
              Reload the page to try again. There is no HTML reader to fall back
              to — the canvas is the document surface.
            </p>
          </div>
        ) : null}
      </div>
    );
  },
);
